import { FunctionComponent } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ROUTE_CONFIG } from "@/routes/config";
import { useAppSelector } from "@/redux/hooks";
import { ErrorPage, FeatureRoute, Loader, MainLayout } from "@/components";
import { ArchivedAccount } from "@/components/ArchivedAccount/ArchivedAccount";
import { ErrorPageType, PermissionRoles } from "@/enum";

import { OnboardingLayout } from "@/components/Layouts/OnboardingLayout";

import { MessagesLayout } from "@/components/Layouts/MessagesLayout";
import { TourProvider } from "@/components/Tour/TourProvider";
import { HelpPanelHost } from "@/components/HelpCenter/HelpPanelHost";
import { PinnedHelpLauncher } from "@/components/HelpCenter/PinnedHelpLauncher";
import { HelpLauncherRegistryProvider } from "@/components/HelpCenter/helpLauncherRegistry";

import { DasboardLayout } from "@/components/Layouts/DashboardLayout";
import { ListLayout } from "@/components/Layouts/ListLayout";

import { HelpCenterAdmin } from "./pages/HelpCenterAdmin/HelpCenterAdmin";

import { FeaturesCodes } from "./pages/Users/enum/codes";

import { authSelector } from "../Public/redux/auth.slice";
import { Account } from "./pages/Account/Account";
import {
	LEGACY_NOTIFICATION_SETTINGS_PATH,
	NOTIFICATION_SETTINGS_DEEP_LINK,
} from "./pages/Account/notificationSettingsDeepLink";
import { Admin } from "./routes/Admin";
import { Companies } from "./pages/Companies/Companies";
import { Dashboard } from "./pages/Dashboard/Dashboard";
import { List } from "./pages/List/List";
import { CustomEvent } from "./pages/CustomEvent/CustomEvent";

import { Score } from "./pages/Score/Score";
import { Company } from "./pages/Company/Company";
import { CopywritingTab } from "./pages/Company/components/CopywritingTab/CopywritingTab";
import { Requests } from "./pages/Request/Requests";
import { Cron } from "./pages/Cron/Cron";
import { BlacklistOverview } from "./pages/Blacklist/pages/BlacklistOverview/BlacklistOverview";

import { ProfileTracker } from "./pages/ProfileTracker/ProfileTracker";
import { Subscription } from "./pages/Subscription/Subscription";
import { Onboarding } from "./pages/Onboarding/Onboarding";
import { SelectSubscription } from "./pages/Usage/pages/SelectSubscription";
import { SelectSubscriptionDone } from "./pages/Usage/pages/SelectSubscriptionDone";
import { OnboardingV3 } from "./pages/OnboardingV3/OnboardingV3";
import { Messages } from "./pages/Messages/Messages";
import { Analytics } from "./pages/Analytics/Analytics";
import { MessageSocketProvider } from "./pages/Messages/providers/SocketContext";
import { DeepResearchSocketProvider } from "./pages/ProfileTracker/providers/DeepResearchSocketContext";
import { NotificationSocketProvider } from "./pages/Notifications/providers/NotificationSocketContext";
import { Templates } from "./pages/Templates/Templates";
import { Signals } from "./pages/Signals/Signals";
import { KnowledgeDb } from "./pages/KnowledgeDb/KnowledgeDb";
import { FeatureUsage } from "./pages/FeatureUsage/FeatureUsage";
import { FeatureManagement } from "./pages/FeatureManagement/FeatureManagement";
import { SupportDashboard } from "./pages/SupportDashboard/SupportDashboard";
import { AdminFixes } from "./pages/AdminFixes/AdminFixes";
import { ApiHealth } from "./pages/ApiHealth/ApiHealth";
import { Campaigns } from "./pages/Campaigns/Campaigns";
import { Support } from "./pages/Support/Support";
import { TicketDetail } from "./pages/Support/pages/TicketDetail/TicketDetail";
import { Tickets } from "./pages/Tickets/Tickets";
import { StaffTicketDetail } from "./pages/Tickets/pages/TicketDetail/StaffTicketDetail";
import { Contacts } from "./pages/Contacts/Contacts";
import { DoNotContact } from "./pages/DoNotContact/DoNotContact";
import { Feed } from "./pages/Feed/Feed";
import { Integrations } from "./pages/Integrations/Integrations";
import { useGetCompanyLifecycleQuery } from "./redux/company/company.api";

const { LOGIN } = ROUTE_CONFIG;

/**
 * Reachable while ARCHIVED/DUE_FOR_DELETION (PREC-3900): the reactivate/billing flow itself and
 * its own post-checkout finalize step. These are the backend's *actual* Stripe redirect targets
 * for a returning customer (`isReturningCustomer` in
 * `apps/api/src/subscription/utils/checkout-session-params.util.ts` — true whenever
 * `company.subscriptions.length > 0`, which every archived company satisfies): both the checkout
 * success and cancel URLs land on `/app/select-subscription[/done]`, never on the unrelated
 * `/subscription` route, so those are the two paths that must stay open while every other route
 * stays walled off.
 */
const ARCHIVED_ALLOWED_ROUTE_PREFIXES = [
	ROUTE_CONFIG.SELECT_SUBSCRIPTION,
	ROUTE_CONFIG.SELECT_SUBSCRIPTION_DONE,
];

export const Private: FunctionComponent = () => {
	const { user } = useAppSelector(authSelector);
	const { data: lifecycle, isLoading: isLifecycleLoading } = useGetCompanyLifecycleQuery(
		undefined,
		{ skip: !user }
	);
	const location = useLocation();

	if (!user) {
		return <Navigate to={LOGIN} />;
	}

	/**
	 * Hold the render until the lifecycle state is known (PREC-3900 whole-branch review,
	 * Critical 2). Mounting `<Routes>` first is not harmless: `MainLayout` fires
	 * `useGetCreditsQuery` -> `GET /api/usage` on its very first render, which is not on the
	 * backend guard's archived allow-list, so an archived company got a 403 `COMPANY_ARCHIVED`
	 * before the lifecycle answer arrived. RTK does not abort that request when this component
	 * re-renders into `<ArchivedAccount/>`, so it fired even when the lifecycle response won the
	 * race — and it used to take the whole SPA to the error page.
	 *
	 * `isLoading` (not `isFetching`) is the right gate: it is true only while the *first* result
	 * is outstanding, so a later refetch never blanks the app out from under the user.
	 */
	if (isLifecycleLoading) {
		return <Loader />;
	}

	// Wall off an archived company behind the reactivate screen (PREC-3900). The grace month is
	// deliberately NOT gated here — it is read-only, not locked, and blocking it would recreate
	// the exact complaint this ticket exists to fix. The reactivate route itself (and its
	// post-checkout finalize step) must stay reachable, or the only way back in is unreachable.
	//
	// This is the *only* wall in the app, and the lifecycle is the only thing that raises it. The
	// four layouts under here used to raise a second one off the credit balance, which is not a
	// statement about access at all: credit buckets expire at the end of the month they were
	// granted in, so a customer who cancelled mid-month lost every page — lead lists included — on
	// the first of the next one, weeks inside the read-only month they were promised (PREC-3944).
	// Never gate a page on `credits <= 0`; a company with no credits can still read its data.
	const isArchived = lifecycle?.state === "ARCHIVED" || lifecycle?.state === "DUE_FOR_DELETION";
	const isOnReactivateRoute = ARCHIVED_ALLOWED_ROUTE_PREFIXES.some((route) =>
		location.pathname.startsWith(route)
	);

	if (isArchived && !isOnReactivateRoute) {
		return <ArchivedAccount deletionAt={lifecycle.deletionAt} />;
	}

	return (
		<MessageSocketProvider>
			<DeepResearchSocketProvider>
				<NotificationSocketProvider>
					<TourProvider>
						<HelpLauncherRegistryProvider>
							<Routes>
								<Route element={<OnboardingLayout />}>
									<Route element={<Subscription />} path="/subscription" />
									<Route element={<Onboarding />} path="/onboarding/*" />
									<Route element={<OnboardingV3 />} path="/onboarding-v3/*" />
									{/* The slim flow lived here before PREC-3719 — keep sessions that are
									    parked on the old path from landing on a blank route. */}
									<Route
										element={<Navigate replace to={ROUTE_CONFIG.ONBOARDING_V3} />}
										path="/onboarding-v2/*"
									/>
									<Route element={<SelectSubscription />} path="/select-subscription" />
									<Route element={<SelectSubscriptionDone />} path="/select-subscription/done" />
								</Route>

								<Route element={<FeatureRoute feature={FeaturesCodes.SEQUENCER} />}>
									<Route element={<MessagesLayout />}>
										<Route element={<Messages />} path="/messages/*" />
									</Route>
								</Route>

								<Route element={<DasboardLayout />}>
									<Route element={<Dashboard />} path="/dashboard/*" />
								</Route>

								<Route element={<ListLayout />}>
									<Route element={<List />} path="/lists/*" />
								</Route>

								<Route element={<MainLayout />}>
									<Route element={<Account />} path="/account" />
									<Route element={<Company />} path="/company" />
									<Route element={<BlacklistOverview isGlobal={true} />} path="/blacklist/global" />
									<Route element={<Admin />} path="/users/*" />
									<Route element={<Requests />} path="/costs/*" />

									<Route element={<Cron />} path="/cron" />
									<Route element={<ApiHealth />} path="/api-health" />

									<Route
										element={
											<FeatureRoute
												companyId={user?.company?.id}
												companyIds={[1]}
												permission={PermissionRoles.GLOBAL_ADMIN}
											/>
										}
									>
										<Route element={<Companies />} path="/companies/*" />
										<Route element={<CustomEvent />} path="/custom-event/*" />
										<Route element={<FeatureManagement />} path="/feature-management" />
										<Route element={<SupportDashboard />} path="/support-dashboard" />
										<Route element={<FeatureUsage />} path="/feature-usage" />
										<Route element={<HelpCenterAdmin />} path="/help-admin/*" />
										<Route element={<AdminFixes />} path="/admin-fixes" />
									</Route>

									{/* Staff = any GLOBAL_ADMIN (PREC-3934 decision 1): permission only, no company gate. */}
									<Route element={<FeatureRoute permission={PermissionRoles.GLOBAL_ADMIN} />}>
										<Route element={<Tickets />} path="/tickets" />
										<Route element={<StaffTicketDetail />} path="/tickets/:id" />
									</Route>

									<Route element={<FeatureRoute feature={FeaturesCodes.LEAD_SCORING} />}>
										<Route element={<Score />} path="/lead-scoring/*" />
										<Route element={<Analytics />} path="/analytics/*" />
									</Route>

									<Route element={<FeatureRoute permission={PermissionRoles.TRACKER} />}>
										<Route element={<ProfileTracker />} path="/profile-tracker/*" />
										<Route element={<Feed />} path="/feed" />
									</Route>

									<Route
										element={
											<FeatureRoute
												feature={FeaturesCodes.TEMPLATES}
												permission={PermissionRoles.TEMPLATES}
											/>
										}
									>
										<Route element={<Templates />} path="/templates/*" />
									</Route>

									<Route
										element={
											<FeatureRoute
												feature={FeaturesCodes.COPYWRITING_SETTINGS}
												permission={PermissionRoles.COPYWRITING_SETTINGS}
											/>
										}
									>
										<Route element={<CopywritingTab />} path="/copywriting" />
									</Route>

									<Route element={<FeatureRoute feature={FeaturesCodes.CAMPAIGNS_OVERVIEW} />}>
										<Route element={<Campaigns />} path="/campaigns" />
									</Route>

									<Route element={<FeatureRoute feature={FeaturesCodes.SUPPORT_TICKETS} />}>
										<Route element={<Support />} path="/support" />
										<Route element={<TicketDetail />} path="/support/:id" />
									</Route>

									<Route element={<FeatureRoute feature={FeaturesCodes.TRACKER_AS_LIST} />}>
										<Route element={<Signals />} path="/signals" />
									</Route>

									<Route element={<FeatureRoute feature={FeaturesCodes.KNOWLEDGE_DB} />}>
										<Route element={<KnowledgeDb />} path="/knowledge-db" />
									</Route>

									<Route
										element={
											<FeatureRoute
												feature={FeaturesCodes.CONTACT}
												permission={PermissionRoles.CONTACT}
											/>
										}
									>
										<Route element={<Contacts />} path="/contacts/*" />
										<Route element={<DoNotContact />} path="/do-not-contact/*" />
									</Route>

									<Route element={<FeatureRoute permission={PermissionRoles.COMPANY} />}>
										<Route element={<Integrations />} path="/integrations/*" />
									</Route>
								</Route>

								{/*
								 * Notification e-mails sent before PREC-3789 link to this long-gone path. They
								 * stay in inboxes forever, so keep forwarding them to the settings tab that
								 * replaced it instead of letting them fall through to NotFound.
								 */}
								<Route
									element={<Navigate replace to={NOTIFICATION_SETTINGS_DEEP_LINK} />}
									path={LEGACY_NOTIFICATION_SETTINGS_PATH}
								/>

								<Route element={<ErrorPage type={ErrorPageType.NotFound} />} path="*" />
								<Route element={<ErrorPage type={ErrorPageType.Forbidden} />} path="/error-page" />
							</Routes>

							{/*
							 * The help chat lives in each page's header. Pages with no header bar get the
							 * same icon in the same corner from PinnedHelpLauncher, and every launcher
							 * shares the one panel below.
							 */}
							<PinnedHelpLauncher />
							<HelpPanelHost />
						</HelpLauncherRegistryProvider>
					</TourProvider>
				</NotificationSocketProvider>
			</DeepResearchSocketProvider>
		</MessageSocketProvider>
	);
};
